import { Video, Rotate3d, Headset, Globe, Landmark, Sparkles, MonitorPlay, Megaphone } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { useLang } from "@/i18n/LanguageContext";
import PageHero from "@/components/site/PageHero";
import ContactForm from "@/components/site/ContactForm";
import { Reveal, SectionHead } from "@/components/site/Reveal";

const CAP_ICONS = [Video, MonitorPlay, Rotate3d, Headset, Globe, Sparkles, Landmark, Megaphone];

const PartnerPage = () => {
  const { t } = useLang();
  useSeo("partner");
  const audiences = t("partner.audiences");
  const capabilities = t("partner.capabilities");

  return (
    <div data-testid="partner-page">
      <PageHero
        testId="partner-hero"
        image="/assets/editorial-sunset.webp"
        eyebrow={t("partner.eyebrow")}
        lines={[t("partner.titleA"), t("partner.titleB")]}
        subtitle={t("partner.heroSub")}
      />

      <section className="py-20 lg:py-28" data-testid="partner-audience">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <SectionHead eyebrow={t("partner.perChiEyebrow")} title={t("partner.perChiTitle")} />
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap gap-3" data-testid="partner-audience-chips">
              {audiences.map((a) => (
                <span key={a} className="border border-[#1C1B18]/15 px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase text-[#1C1B18]/80 hover:border-[#C5A059] hover:text-[#9E7B36] transition-colors duration-500">
                  {a}
                </span>
              ))}
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 mt-16" data-testid="partner-capabilities">
            {capabilities.map((label, i) => {
              const Icon = CAP_ICONS[i];
              return (
                <Reveal key={label} delay={0.05 * i}>
                  <div className="flex items-start gap-4 py-5 border-t border-[#1C1B18]/10">
                    <Icon size={18} strokeWidth={1.4} className="text-[#9E7B36] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#1C1B18] leading-relaxed">{label}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#EFECE6]" data-testid="partner-form-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <SectionHead eyebrow={t("partner.formEyebrow")} title={<>{t("partner.formTitleA")}<br />{t("partner.formTitleB")}</>} />
            <Reveal delay={0.15}>
              <p className="mt-8 text-base leading-relaxed text-[#666157]">
                {t("partner.formBody")}
              </p>
              <p className="font-serif-display italic text-xl text-[#9E7B36] mt-8">
                {t("partner.quote")}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.2}>
              <ContactForm source="partner" />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerPage;
